import React, { useState, useEffect, useCallback } from 'react';
import { Staff } from '../../types';
import { X, Plus, Shield, Users, GraduationCap, Settings, DollarSign, Eye, EyeOff, AlertCircle, CheckCircle, CreditCard, Loader, Wallet } from 'lucide-react';
import { AUTHORITIES, ROLE_TEMPLATES, UserRole, getAuthoritiesByCategory } from '../../types/authorities';
import { useToast } from '../../contexts/ToastContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import { startCardRegistration, cancelCardRegistration } from '../../services/rfidService';
import { FinanceAccount, FinanceService } from '../../services/finance.service';

interface StaffFormProps {
    staff?: Staff;
    onSubmit: (staff: Omit<Staff, 'id' | 'createdAt' | 'updatedAt'>) => void;
    onCancel: () => void;
    loading?: boolean;
}

const StaffForm: React.FC<StaffFormProps> = ({ staff, onSubmit, onCancel, loading = false }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [checkingStaffId, setCheckingStaffId] = useState(false);
    const [staffIdError, setStaffIdError] = useState('');
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const { showError, showSuccess } = useToast();

    const [formData, setFormData] = useState({
        staffId: staff?.staffId || '',
        firstName: staff?.firstName || '',
        lastName: staff?.lastName || '',
        email: staff?.email || '',
        phone: staff?.phone || '',
        dateOfBirth: staff?.dateOfBirth ? staff.dateOfBirth.toISOString().split('T')[0] : '',
        gender: staff?.gender || 'male',
        password: '',
        confirmPassword: '',
        role: (staff?.role || 'clerk') as UserRole,
        address: {
            street: staff?.address.street || '',
            city: staff?.address.city || '',
            state: staff?.address.state || '',
            zipCode: staff?.address.zipCode || '',
            country: staff?.address.country || '',
        },
        qualification: {
            education: staff?.qualification.education || '',
            certifications: staff?.qualification.certifications || [],
        },
        experience: {
            totalYears: staff?.experience.totalYears || 0,
            previousEmployers: staff?.experience.previousEmployers || [],
        },
        currentPosition: {
            department: staff?.currentPosition.department || '',
            designation: staff?.currentPosition.designation || '',
            joiningDate: staff?.currentPosition.joiningDate ? staff.currentPosition.joiningDate.toISOString().split('T')[0] : '',
            salary: staff?.currentPosition.salary || 0,
            status: staff?.currentPosition.status || 'active',
        },
        emergencyContact: {
            name: staff?.emergencyContact.name || '',
            relationship: staff?.emergencyContact.relationship || '',
            phone: staff?.emergencyContact.phone || '',
            email: staff?.emergencyContact.email || '',
        },
        bankDetails: {
            accountNumber: staff?.bankDetails.accountNumber || '',
            bankName: staff?.bankDetails.bankName || '',
            ifscCode: staff?.bankDetails.ifscCode || '',
            accountHolderName: staff?.bankDetails.accountHolderName || '',
        },
        authorities: staff?.authorities || {},
        cardNumber: staff?.cardNumber || '',
    });

    const [newCertification, setNewCertification] = useState('');
    const [staffIdValid, setStaffIdValid] = useState(false);
    const [emailValid, setEmailValid] = useState(false);

    // Finance Account Management (global accounts used by Revenue / Transfers)
    const [financeAccounts, setFinanceAccounts] = useState<FinanceAccount[]>([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountForm, setAccountForm] = useState<{
        type: 'bank' | 'cash';
        cashLocation: 'college' | 'school';
        name: string;
        openingBalance: number;
        bankName: string;
        accountNumber: string;
        accountTitle: string;
    }>({
        type: 'cash',
        cashLocation: 'college',
        name: 'Cash in Hand (College)',
        openingBalance: 0,
        bankName: '',
        accountNumber: '',
        accountTitle: ''
    });
    
    // RFID Card Registration States
    const [isRegisteringCard, setIsRegisteringCard] = useState(false);
    const [cardRegistrationStatus, setCardRegistrationStatus] = useState<'idle' | 'waiting' | 'success' | 'error'>('idle');
    const [cardRegistrationError, setCardRegistrationError] = useState('');

    const authoritiesByCategory = getAuthoritiesByCategory(AUTHORITIES);

    const refreshFinanceAccounts = useCallback(async () => {
        try {
            setAccountsLoading(true);
            const data = await FinanceService.getAccounts();
            setFinanceAccounts(data);
        } catch (error) {
            showError('Error', 'Failed to load finance accounts');
        } finally {
            setAccountsLoading(false);
        }
    }, [showError]);

    useEffect(() => {
        refreshFinanceAccounts();
    }, [refreshFinanceAccounts]);

    // Auto-validate Staff ID when user types (with debounce)
    useEffect(() => {
        if (!staff && formData.staffId.trim()) {
            const timer = setTimeout(() => {
                checkStaffIdUnique(formData.staffId);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.staffId]);

    // Auto-validate Email when user types (with debounce)
    useEffect(() => {
        if (!staff && formData.email.trim()) {
            const timer = setTimeout(() => {
                checkEmailUnique(formData.email);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.email]);

    // Check if staff ID already exists
    const checkStaffIdUnique = async (staffId: string) => {
        if (!staffId.trim()) {
            setStaffIdError('');
            return true;
        }

        // Skip check if editing and ID hasn't changed
        if (staff && staff.staffId === staffId) {
            setStaffIdError('');
            return true;
        }

        try {
            setCheckingStaffId(true);
            const staffRef = collection(db, 'staff');
            const q = query(staffRef, where('staffId', '==', staffId.trim()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setStaffIdError('This Staff ID already exists. Please use a unique ID.');
                setStaffIdValid(false);
                return false;
            } else {
                setStaffIdError('');
                setStaffIdValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking staff ID:', error);
            setStaffIdError('Error validating Staff ID. Please try again.');
            setStaffIdValid(false);
            return false;
        } finally {
            setCheckingStaffId(false);
        }
    };

    // Check if email already exists
    const checkEmailUnique = async (email: string) => {
        if (!email.trim()) {
            setEmailError('');
            return true;
        }

        // Skip check if editing and email hasn't changed
        if (staff && staff.email === email) {
            setEmailError('');
            return true;
        }

        try {
            setCheckingEmail(true);
            const staffRef = collection(db, 'staff');
            const q = query(staffRef, where('email', '==', email.trim().toLowerCase()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setEmailError('This email is already registered. Please use a different email.');
                setEmailValid(false);
                return false;
            } else {
                setEmailError('');
                setEmailValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking email:', error);
            setEmailError('Error validating email. Please try again.');
            setEmailValid(false);
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Clear errors and valid states when user types
        if (name === 'staffId') {
            setStaffIdError('');
            setStaffIdValid(false);
        }
        if (name === 'email') {
            setEmailError('');
            setEmailValid(false);
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev[parent as keyof typeof prev] as any),
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleRoleChange = (role: UserRole) => {
        setFormData(prev => ({
            ...prev,
            role,
            authorities: ROLE_TEMPLATES[role].reduce((acc, authority) => {
                acc[authority] = true;
                return acc;
            }, {} as { [key: string]: boolean })
        }));
    };

    const handleAuthorityChange = (authorityId: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            authorities: {
                ...prev.authorities,
                [authorityId]: checked
            }
        }));
    };

    const addCertification = () => {
        if (newCertification.trim()) {
            setFormData(prev => ({
                ...prev,
                qualification: {
                    ...prev.qualification,
                    certifications: [...prev.qualification.certifications, newCertification.trim()]
                }
            }));
            setNewCertification('');
        }
    };

    const removeCertification = (index: number) => {
        setFormData(prev => ({
            ...prev,
            qualification: {
                ...prev.qualification,
                certifications: prev.qualification.certifications.filter((_, i) => i !== index)
            }
        }));
    };

    // Check if card number is already registered
    const checkCardNumberExists = async (cardNumber: string): Promise<boolean> => {
        try {
            // Check in staff collection
            const staffRef = collection(db, 'staff');
            const staffQuery = query(staffRef, where('cardNumber', '==', cardNumber));
            const staffSnapshot = await getDocs(staffQuery);

            // Check in teachers collection
            const teachersRef = collection(db, 'teachers');
            const teachersQuery = query(teachersRef, where('cardNumber', '==', cardNumber));
            const teachersSnapshot = await getDocs(teachersQuery);

            // If editing, exclude current staff member
            if (staff) {
                const staffDocs = staffSnapshot.docs.filter(doc => doc.id !== staff.id);
                return staffDocs.length > 0 || !teachersSnapshot.empty;
            }

            return !staffSnapshot.empty || !teachersSnapshot.empty;
        } catch (error) {
            console.error('Error checking card number:', error);
            return false;
        }
    };

    // Handle card registration
    const handleRegisterCard = async () => {
        setIsRegisteringCard(true);
        setCardRegistrationStatus('waiting');
        setCardRegistrationError('');

        try {
            // Start card registration on ESP32
            const cardNumber = await startCardRegistration();

            // Check if card is already registered
            const cardExists = await checkCardNumberExists(cardNumber);

            if (cardExists) {
                setCardRegistrationError('This card is already registered to another user.');
                setCardRegistrationStatus('error');
                setIsRegisteringCard(false);
                showError('Card Already Registered', 'This RFID card is already registered to another user. Please use a different card.');
                return;
            }

            // Card is available, register it
            setFormData(prev => ({
                ...prev,
                cardNumber: cardNumber
            }));
            setCardRegistrationStatus('success');
            showSuccess('Card Registered', `RFID card ${cardNumber} has been successfully registered.`);
        } catch (error: any) {
            console.error('Error registering card:', error);
            setCardRegistrationError(error.message || 'Failed to register card. Please try again.');
            setCardRegistrationStatus('error');
            showError('Card Registration Failed', error.message || 'Failed to register card. Please check ESP32 connection and try again.');
        } finally {
            setIsRegisteringCard(false);
            // Reset status after 3 seconds
            setTimeout(() => {
                setCardRegistrationStatus('idle');
                setCardRegistrationError('');
            }, 3000);
        }
    };

    // Handle cancel card registration
    const handleCancelCardRegistration = async () => {
        try {
            await cancelCardRegistration();
            setIsRegisteringCard(false);
            setCardRegistrationStatus('idle');
            setCardRegistrationError('');
        } catch (error) {
            console.error('Error canceling card registration:', error);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (isRegisteringCard) {
                cancelCardRegistration();
            }
        };
    }, [isRegisteringCard]);



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Staff ID validation
        if (!formData.staffId.trim()) {
            showError('Staff ID Required', 'Please enter a Staff ID.');
            return;
        }

        // Check if Staff ID is unique
        const isStaffIdUnique = await checkStaffIdUnique(formData.staffId);
        if (!isStaffIdUnique) {
            showError('Duplicate Staff ID', 'This Staff ID already exists. Please use a unique ID.');
            return;
        }

        // Email validation
        if (!formData.email.trim()) {
            showError('Email Required', 'Please enter an email address.');
            return;
        }

        // Check if Email is unique
        const isEmailUnique = await checkEmailUnique(formData.email);
        if (!isEmailUnique) {
            showError('Duplicate Email', 'This email is already registered. Please use a different email.');
            return;
        }

        // Password validation for new staff
        if (!staff) {
            if (!formData.password || formData.password.length < 6) {
                showError('Password Too Short', 'Password must be at least 6 characters long.');
                return;
            }
            if (formData.password !== formData.confirmPassword) {
                showError('Passwords Mismatch', 'Passwords do not match. Please check and try again.');
                return;
            }
        }

        const staffData = {
            ...formData,
            dateOfBirth: new Date(formData.dateOfBirth),
            currentPosition: {
                ...formData.currentPosition,
                joiningDate: new Date(formData.currentPosition.joiningDate),
            },
        };

        onSubmit(staffData);
    };

    const quickPermissionToggles: Array<{ id: string; name: string; description: string }> = [
        { id: 'finance_accounts', name: 'Manage Accounts', description: 'Create and manage bank/cash accounts' },
        { id: 'revenue_receive', name: 'Receive Revenue', description: 'Record revenue into selected accounts' },
        { id: 'petty_cash_transfer', name: 'Transfer to Petty Cash', description: 'Move funds from an account into petty cash' },
        { id: 'petty_cash_approve', name: 'Approve Petty Cash', description: 'Approve/reject petty cash expenses' },
        { id: 'finance_ledger', name: 'View Ledger', description: 'View overall finance ledger' },
    ];

    const handleCreateFinanceAccount = async () => {
        try {
            if (!accountForm.name.trim()) {
                showError('Name Required', 'Please provide an account name.');
                return;
            }

            const openingBalance = Number(accountForm.openingBalance || 0);
            if (!Number.isFinite(openingBalance) || openingBalance < 0) {
                showError('Invalid Balance', 'Opening balance must be 0 or greater.');
                return;
            }

            if (accountForm.type === 'bank') {
                if (!accountForm.bankName.trim()) {
                    showError('Bank Name Required', 'Please enter bank name.');
                    return;
                }
                if (!accountForm.accountNumber.trim()) {
                    showError('Account Number Required', 'Please enter account number.');
                    return;
                }
            }

            await FinanceService.createAccount({
                type: accountForm.type,
                name: accountForm.name.trim(),
                openingBalance,
                ...(accountForm.type === 'bank'
                    ? {
                        bankName: accountForm.bankName.trim(),
                        accountNumber: accountForm.accountNumber.trim(),
                        accountTitle: accountForm.accountTitle.trim(),
                    }
                    : {})
            });

            showSuccess('Account Created', 'Finance account created successfully.');
            setAccountForm(prev => ({
                ...prev,
                name: prev.type === 'cash'
                    ? (prev.cashLocation === 'college' ? 'Cash in Hand (College)' : 'Cash in Hand (School)')
                    : '',
                openingBalance: 0,
                bankName: '',
                accountNumber: '',
                accountTitle: ''
            }));
            refreshFinanceAccounts();
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Failed to create account';
            showError('Error', msg);
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'management': return Shield;
            case 'academic': return GraduationCap;
            case 'financial': return DollarSign;
            case 'operations': return Settings;
            default: return Users;
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={onCancel}
            title={staff ? 'Edit Staff Member' : 'Add New Staff Member'}
            size="full"
        >
            {/* Loading Overlay */}
            {loading && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                        <div className="text-gray-700 font-medium">
                            {staff ? 'Updating staff...' : 'Creating staff account...'}
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className={`space-y-8 relative ${loading ? 'pointer-events-none opacity-50' : ''}`}>
                {/* Basic Information */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <Users className="h-6 w-6 mr-3 text-emerald-600" />
                        Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Staff ID <span className="text-red-500">*</span>
                                {staff && (
                                    <span className="text-xs text-gray-500 ml-1">(Cannot be changed)</span>
                                )}
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="staffId"
                                    value={formData.staffId}
                                    onChange={handleInputChange}
                                    placeholder="Enter unique staff ID"
                                    required
                                    readOnly={!!staff}
                                    disabled={checkingStaffId}
                                    className={`w-full px-3 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${staff
                                        ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                        : staffIdError
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                {!staff && checkingStaffId && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                    </div>
                                )}
                                {!staff && !checkingStaffId && staffIdError && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                )}
                                {!staff && !checkingStaffId && !staffIdError && staffIdValid && formData.staffId.trim() && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {!staff && staffIdError && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {staffIdError}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email <span className="text-red-500">*</span>
                                {staff && (
                                    <span className="text-xs text-gray-500 ml-1">(Cannot be changed)</span>
                                )}
                            </label>
                            <div className="relative">
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email address"
                                    required
                                    readOnly={!!staff}
                                    disabled={checkingEmail}
                                    className={`w-full px-3 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${staff
                                        ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                        : emailError
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                {!staff && checkingEmail && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                    </div>
                                )}
                                {!staff && !checkingEmail && emailError && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                )}
                                {!staff && !checkingEmail && !emailError && emailValid && formData.email.trim() && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {!staff && emailError && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {emailError}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date of Birth <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Gender <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            >
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        {!staff && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="Create a secure password"
                                            required={!staff}
                                            className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm Password <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            placeholder="Confirm your password"
                                            required={!staff}
                                            className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Card>

                {/* Role & Authority Management */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <Shield className="h-6 w-6 mr-3 text-emerald-600" />
                        Role & Authority Management
                    </h3>

                    {/* Role Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Select Role</label>
                        <p className="text-sm text-gray-500 mb-4">
                            Note: Teachers and Students have separate forms. Use this form for administrative and support staff only.
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {Object.keys(ROLE_TEMPLATES)
                                .filter(role => role !== 'teacher' && role !== 'student') // Exclude teacher and student roles
                                .map((role) => (
                                    <button
                                        key={role}
                                        type="button"
                                        onClick={() => handleRoleChange(role as UserRole)}
                                        className={`p-3 rounded-xl border-2 transition-all duration-200 ${formData.role === role
                                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                            : 'border-gray-200 hover:border-emerald-300'
                                            }`}
                                    >
                                        <div className="font-medium capitalize">{role}</div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {ROLE_TEMPLATES[role as UserRole].length} permissions
                                        </div>
                                    </button>
                                ))}
                        </div>
                    </div>

                    {/* Authority Management */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Customize Permissions</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {Object.entries(authoritiesByCategory).map(([category, authorities]) => {
                                const CategoryIcon = getCategoryIcon(category);
                                return (
                                    <div key={category} className="space-y-3">
                                        <h4 className="font-medium text-gray-900 flex items-center">
                                            <CategoryIcon className="h-4 w-4 mr-2 text-emerald-600" />
                                            {category.charAt(0).toUpperCase() + category.slice(1)}
                                        </h4>
                                        <div className="space-y-2">
                                            {authorities.map((authority) => (
                                                <label key={authority.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.authorities[authority.id] || false}
                                                        onChange={(e) => handleAuthorityChange(authority.id, e.target.checked)}
                                                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium text-gray-900">{authority.name}</div>
                                                        <div className="text-xs text-gray-500">{authority.description}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Card>

                {/* App Permission (Quick Finance Permissions) */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                        <Shield className="h-6 w-6 mr-3 text-emerald-600" />
                        App Permission
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                        Quick toggles for key finance permissions (these map to the same permissions above).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {quickPermissionToggles.map(p => (
                            <label key={p.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={formData.authorities[p.id] || false}
                                    onChange={(e) => handleAuthorityChange(p.id, e.target.checked)}
                                    className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-gray-900">{p.name}</div>
                                    <div className="text-xs text-gray-500">{p.description}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </Card>

                {/* Account Management */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                        <Wallet className="h-6 w-6 mr-3 text-emerald-600" />
                        Account Management
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                        Create Bank or Cash accounts. Revenue entries and petty cash transfers will credit/debit these accounts.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Select
                            label="Account Type"
                            name="accountType"
                            value={accountForm.type}
                            onChange={(e) => {
                                const type = e.target.value as 'bank' | 'cash';
                                setAccountForm(prev => ({
                                    ...prev,
                                    type,
                                    name: type === 'cash'
                                        ? (prev.cashLocation === 'college' ? 'Cash in Hand (College)' : 'Cash in Hand (School)')
                                        : prev.name
                                }));
                            }}
                            options={[
                                { value: 'cash', label: 'Cash (Cash in Hand)' },
                                { value: 'bank', label: 'Bank' }
                            ]}
                        />

                        {accountForm.type === 'cash' ? (
                            <Select
                                label="Cash In Hand"
                                name="cashLocation"
                                value={accountForm.cashLocation}
                                onChange={(e) => {
                                    const loc = e.target.value as 'college' | 'school';
                                    setAccountForm(prev => ({
                                        ...prev,
                                        cashLocation: loc,
                                        name: loc === 'college' ? 'Cash in Hand (College)' : 'Cash in Hand (School)'
                                    }));
                                }}
                                options={[
                                    { value: 'college', label: 'College' },
                                    { value: 'school', label: 'School' }
                                ]}
                            />
                        ) : (
                            <div />
                        )}

                        <Input
                            label="Account Name"
                            value={accountForm.name}
                            onChange={(e) => setAccountForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={accountForm.type === 'cash' ? 'Cash in Hand (College)' : 'e.g. Meezan Bank - Main'}
                            required
                        />

                        <Input
                            label="Opening Balance (PKR)"
                            type="number"
                            value={accountForm.openingBalance}
                            onChange={(e) => setAccountForm(prev => ({ ...prev, openingBalance: parseFloat(e.target.value) }))}
                        />

                        {accountForm.type === 'bank' && (
                            <>
                                <Input
                                    label="Bank Name"
                                    value={accountForm.bankName}
                                    onChange={(e) => setAccountForm(prev => ({ ...prev, bankName: e.target.value }))}
                                    required
                                />
                                <Input
                                    label="Account Number"
                                    value={accountForm.accountNumber}
                                    onChange={(e) => setAccountForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                                    required
                                />
                                <Input
                                    label="Account Title (Optional)"
                                    value={accountForm.accountTitle}
                                    onChange={(e) => setAccountForm(prev => ({ ...prev, accountTitle: e.target.value }))}
                                />
                            </>
                        )}
                    </div>

                    <div className="flex justify-end mt-6">
                        <Button
                            type="button"
                            onClick={handleCreateFinanceAccount}
                            loading={accountsLoading}
                            disabled={accountsLoading}
                        >
                            Save Account
                        </Button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-900">Existing Accounts</h4>
                            <Button type="button" variant="secondary" size="sm" onClick={refreshFinanceAccounts} loading={accountsLoading}>
                                Refresh
                            </Button>
                        </div>
                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-100">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">Type</th>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">Account</th>
                                        <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-400 uppercase tracking-widest">Balance</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-50">
                                    {financeAccounts.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="px-4 py-4 text-sm text-gray-500">
                                                No accounts yet. Create Cash in Hand (College/School) and your bank accounts here.
                                            </td>
                                        </tr>
                                    ) : (
                                        financeAccounts.map(a => (
                                            <tr key={a.id}>
                                                <td className="px-4 py-2 text-xs text-gray-600 capitalize">{a.type}</td>
                                                <td className="px-4 py-2 text-sm font-medium text-gray-900">{a.name}</td>
                                                <td className="px-4 py-2 text-sm text-right font-bold text-emerald-700">PKR {Number(a.balance ?? 0).toLocaleString()}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Card>

                {/* Address Information */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Address Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Street Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.street"
                                value={formData.address.street}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                City <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                State <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                ZIP Code <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.zipCode"
                                value={formData.address.zipCode}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Country <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.country"
                                value={formData.address.country}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                    </div>
                </Card>

                {/* Professional Information */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Professional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Education <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="qualification.education"
                                value={formData.qualification.education}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Total Experience (Years) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="experience.totalYears"
                                value={formData.experience.totalYears}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Department <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="currentPosition.department"
                                value={formData.currentPosition.department}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Designation <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="currentPosition.designation"
                                value={formData.currentPosition.designation}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Joining Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="currentPosition.joiningDate"
                                value={formData.currentPosition.joiningDate}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Salary <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="currentPosition.salary"
                                value={formData.currentPosition.salary}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="currentPosition.status"
                                value={formData.currentPosition.status}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="resigned">Resigned</option>
                            </select>
                        </div>
                    </div>

                    {/* Certifications */}
                    <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Certifications</label>
                        <div className="flex space-x-2 mb-3">
                            <input
                                type="text"
                                value={newCertification}
                                onChange={(e) => setNewCertification(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                placeholder="Add certification"
                            />
                            <Button type="button" onClick={addCertification} variant="secondary">
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {formData.qualification.certifications.map((cert, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 text-emerald-800"
                                >
                                    {cert}
                                    <button
                                        type="button"
                                        onClick={() => removeCertification(index)}
                                        className="ml-2 hover:text-emerald-600"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                </Card>

                {/* Emergency Contact */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Emergency Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                            label="Contact Name"
                            name="emergencyContact.name"
                            value={formData.emergencyContact.name}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Relationship"
                            name="emergencyContact.relationship"
                            value={formData.emergencyContact.relationship}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Phone"
                            name="emergencyContact.phone"
                            value={formData.emergencyContact.phone}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Email"
                            name="emergencyContact.email"
                            type="email"
                            value={formData.emergencyContact.email}
                            onChange={handleInputChange}
                        />
                    </div>
                </Card>

                {/* RFID Card Registration */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <CreditCard className="h-6 w-6 mr-3 text-emerald-600" />
                        RFID Card Registration
                    </h3>
                    <div className="space-y-4">
                        {formData.cardNumber ? (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-green-900">Card Registered</p>
                                        <p className="text-sm text-green-700 mt-1">Card Number: <span className="font-mono font-semibold">{formData.cardNumber}</span></p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={handleRegisterCard}
                                        disabled={isRegisteringCard}
                                        className="flex items-center space-x-2"
                                    >
                                        <CreditCard className="h-4 w-4" />
                                        <span>Change Card</span>
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                                <p className="text-sm text-gray-700 mb-4">No RFID card registered. Click the button below to register a card.</p>
                                <Button
                                    type="button"
                                    onClick={handleRegisterCard}
                                    disabled={isRegisteringCard}
                                    loading={isRegisteringCard}
                                    className="flex items-center space-x-2"
                                >
                                    <CreditCard className="h-4 w-4" />
                                    <span>{isRegisteringCard ? 'Waiting for Card...' : 'Register Card'}</span>
                                </Button>
                            </div>
                        )}

                        {cardRegistrationStatus === 'waiting' && (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                <div className="flex items-center space-x-3">
                                    <Loader className="h-5 w-5 text-blue-600 animate-spin" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-900">Waiting for Card...</p>
                                        <p className="text-xs text-blue-700 mt-1">Please swipe your RFID card on the reader</p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleCancelCardRegistration}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {cardRegistrationStatus === 'error' && cardRegistrationError && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                                <div className="flex items-center space-x-2">
                                    <AlertCircle className="h-5 w-5 text-red-600" />
                                    <p className="text-sm text-red-700">{cardRegistrationError}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Bank Details */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Bank Details</h3>
                    <p className="text-sm text-gray-500 mb-6">Optional - Can be added later</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                            label="Account Number"
                            name="bankDetails.accountNumber"
                            value={formData.bankDetails.accountNumber}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Bank Name"
                            name="bankDetails.bankName"
                            value={formData.bankDetails.bankName}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="IFSC Code"
                            name="bankDetails.ifscCode"
                            value={formData.bankDetails.ifscCode}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Account Holder Name"
                            name="bankDetails.accountHolderName"
                            value={formData.bankDetails.accountHolderName}
                            onChange={handleInputChange}
                        />
                    </div>
                </Card>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={loading || checkingStaffId || checkingEmail}
                        disabled={loading || checkingStaffId || checkingEmail || !!staffIdError || !!emailError}
                    >
                        {loading ? 'Saving...' : staff ? 'Update Staff' : 'Add Staff'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default StaffForm;